import { radioStyles } from './radio.styles';
import { FASTElement, Notifier, observable, Observable } from '@microsoft/fast-element';
import { checkIcon } from '../../styles/icons';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { CheckableFormAssociated } from '../../foundation/form-associated';
import { RadioControl } from './radio.options';
import { NatGenRadioGroup } from '../radio-group/radio-group';
import { keySpace } from '@microsoft/fast-web-utilities';
import { radioTemplate } from './radio.template';

class _Radio extends FASTElement {}
interface _Radio extends CheckableFormAssociated {}

/**
 * A form-associated base class for the {@link @microsoft/fast-foundation#(FASTRadio:class)} component.
 *
 * @beta
 */
export class FormAssociatedRadio extends CheckableFormAssociated(_Radio) {
    proxy = document.createElement("input");
}

/**
 * @element natgen-radio
 */
export class NatGenRadio extends FormAssociatedRadio implements RadioControl {
    protected checkedChangedNotifier: Notifier;

    connectedCallback() {
        super.connectedCallback();
        this.validate();

        if (
            this.parentElement?.getAttribute("role") !== "radiogroup" &&
            this.getAttribute("tabindex") === null
        ) {
            if (!this.disabled) {
                this.setAttribute("tabindex", "0");
            }
        }

        if (this.checkedAttribute) {
            if (!this.dirtyChecked) {
                // Setting this.checked will cause us to enter a dirty state,
                // but if we are clean when defaultChecked is changed, we want to stay
                // in a clean state, so reset this.dirtyChecked
                if (!this.isInsideRadioGroup()) {
                    this.checked = this.defaultChecked ?? false;
                    this.dirtyChecked = false;
                }
            }
        }

        this.checkedChangedNotifier = Observable.getNotifier(this);
        this.checkedChangedNotifier.subscribe({ handleChange: this.checkedChangeHandler.bind(this) }, 'checked');
    }

    protected checkedChangeHandler(source: NatGenRadio, propertyName: string) {
        if (propertyName === 'checked' && source.checked) {
            (source.getRootNode() as Element).querySelectorAll(`${this.tagName}[aria-checked=true][name=${source.name}]`).forEach(r => {
                if (r != source)
                    (r as NatGenRadio).checked = false;
            });
        }
    }

    /**
     * The name of the radio.
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefname | name attribute} for more info.
     */
    @observable
    public name: string;

    /**
     * The element's value to be included in form submission when checked.
     * Default to "on" to reach parity with input[type="radio"]
     *
     * @internal
     */
    public initialValue: string = "on";

    /**
     * @internal
     */
    @observable
    public defaultSlottedNodes: Node[];

    private get radioGroup() {
        return (this as HTMLElement).closest(
            "[role=radiogroup]"
        ) as NatGenRadioGroup | null;
    }

    /**
     * @internal
     */
    public defaultCheckedChanged(): void {
        if (this.$fastController.isConnected && !this.dirtyChecked) {
            // Setting this.checked will cause us to enter a dirty state,
            // but if we are clean when defaultChecked is changed, we want to stay
            // in a clean state, so reset this.dirtyChecked
            if (!this.isInsideRadioGroup()) {
                this.checked = this.defaultChecked ?? false;
                this.dirtyChecked = false;
            }
        }
    }

    constructor() {
        super();
        this.proxy.setAttribute("type", "radio");
    }

    private isInsideRadioGroup(): boolean {
        return this.radioGroup !== null;
    }

    /**
     * Handles key presses on the radio.
     */
    public keypressHandler(e: KeyboardEvent): boolean | void {
        switch (e.key) {
            case keySpace:
                if (!this.checked && !this.radioGroup?.readOnly) {
                    this.checked = true;
                }
                return;
        }

        return true;
    }

    /**
     * Handles key presses on the radio.
     */
    public clickHandler(e: MouseEvent): boolean | void {
        if (!this.checked && !this.radioGroup?.readOnly) {
            this.checked = true;
        }
    }
}

export const natGenRadioDefinition = NatGenRadio.compose({
    name: `${NatGenDesignSystem.prefix}-radio`,
    template: radioTemplate({
        checkedIndicator: checkIcon
    }),
    styles: radioStyles
});
